import React from "react";
import { IconButton as MaterialIconButton } from "@material-ui/core";
import makeStyles from "@material-ui/core/styles/makeStyles";
import { Theme } from "@material-ui/core/styles";
import { SvgIconComponent } from "@material-ui/icons";
import { ApplicationColorPalette, applicationPalettes, applicationShadowPalettes } from "shared/theme/defaultTheme";
import { ButtonSize, CommonButtonsProps } from "./commonButtonsProps";

interface IconButtonStyleProps {
    palette: ApplicationColorPalette;
    shadowPalette?: ApplicationColorPalette;
    iconPalette: ApplicationColorPalette;
    activePalette: ApplicationColorPalette;
    hoverPalette: ApplicationColorPalette;
    size: ButtonSize;
}

export interface IconButtonProps extends CommonButtonsProps {
    id?: string;
    icon: SvgIconComponent;
    iconPalette?: ApplicationColorPalette;
    hoverPalette?: ApplicationColorPalette;
    activePalette?: ApplicationColorPalette;
    noShadow?: boolean;
    size?: ButtonSize;
    buttonClasses?: Record<string, string>;
    iconClasses?: Record<string, string>;
}

const useStyles = makeStyles<Theme, IconButtonStyleProps>({
    icon: {
        fontSize: "inherit",
        color: "inherit",
    },
    root: {
        fontSize: "24px",
        boxShadow: ({ shadowPalette }) => (shadowPalette ? applicationShadowPalettes[shadowPalette][16] : "none"),
        color: ({ iconPalette }) => applicationPalettes[iconPalette]["400"],
        cursor: "pointer",
        "@media (hover: hover)": {
            "&:hover": {
                boxShadow: ({ shadowPalette }) =>
                    shadowPalette ? applicationShadowPalettes[shadowPalette][4] : "none",
                backgroundColor: ({ hoverPalette }) => applicationPalettes[hoverPalette]["50"],
                color: ({ hoverPalette }) => applicationPalettes[hoverPalette]["500"],
            },
        },
        "&:active": {
            boxShadow: ({ shadowPalette }) => (shadowPalette ? applicationShadowPalettes[shadowPalette][2] : "none"),
            backgroundColor: ({ activePalette }) => applicationPalettes[activePalette]["50"],
            color: ({ activePalette }) => applicationPalettes[activePalette]["500"],
        },
        "&$disabled": {
            color: ({ palette }) => applicationPalettes[palette]["200"],
        },
    },
    disabled: {
        boxShadow: ({ shadowPalette }) => (shadowPalette ? applicationShadowPalettes[shadowPalette][1] : "none"),
    },
});

export default function IconButton({
    id,
    icon,
    iconPalette,
    palette,
    activePalette,
    hoverPalette,
    noShadow,
    size,
    reference,
    buttonClasses,
    iconClasses,
    href,
    openInNewTab,
    ...rest
}: IconButtonProps): JSX.Element {
    const { icon: iconRoot, ...classes } = useStyles({
        palette: palette || "default",
        shadowPalette: noShadow ? undefined : palette || "default",
        iconPalette: iconPalette || palette || "default",
        activePalette: activePalette || palette || "default",
        hoverPalette: hoverPalette || activePalette || palette || "default",
        size: size || "s",
    });
    const Icon = icon;
    const additionalProps = href ? { href, target: openInNewTab ? "_blank" : "_self" } : {};

    return (
        <MaterialIconButton
            id={id}
            classes={{ ...classes, ...buttonClasses }}
            size={size === "s" ? "small" : "medium"}
            disableRipple
            {...rest}
            {...additionalProps}
        >
            <Icon classes={{ root: iconRoot, ...iconClasses }} />
        </MaterialIconButton>
    );
}
